import "material-icons/iconfont/material-icons.css";
import "primeflex/primeflex.css";
import "primeicons/primeicons.css"; // Icons
import { PrimeReactProvider } from "primereact/api";
import "primereact/resources/primereact.min.css"; // Core CSS
import "primereact/resources/themes/nova/theme.css"; // Theme
import React from "react";
import ReactDOM from "react-dom/client";
import {
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
} from "react-router-dom";
import App from "./App";
import "./assets/css/variables.css";
import { ToastProvider } from "./common_nextgen/hooks/ToastContext";
import {
  PRICE_SCHEDULE_MODES,
  ROUTE_PATHS,
} from "./common_nextgen/utils/Constants";
import "./index.css";
import reportWebVitals from "./reportWebVitals";

import { AuthProvider } from "./common_nextgen/hooks/AuthContext";
import { ModalProvider } from "./common_nextgen/hooks/ModalContext";
import { RBACProvider } from "./common_nextgen/hooks/RBACContext";
import { LoaderProvider } from "./common_nextgen/hooks/LoaderContext";

import AdditionalPatientPay from "./pages/AdditionalPatientPay/AdditionalPatientPay";
import AdditionalPatientPayNotes from "./pages/AdditionalPatientPay/AdditionalPatientPayNotes";
import AdditionalPatientPayScheduleForm from "./pages/AdditionalPatientPay/AdditionalPatientPayScheduleForm";
import AdditionalPatientPaySteps from "./pages/AdditionalPatientPay/AdditionalPatientPaySteps";
import CopaySchedule from "./pages/CopaySchedule/CopaySchedule";
import CopayScheduleForm from "./pages/CopaySchedule/CopayScheduleForm";
import CopayScheduleStep from "./pages/CopaySchedule/CopayScheduleStep";
import CopyCopaySchedule from "./pages/CopaySchedule/CopyCopaySchedule";
import CouponPatientPayGPIList from "./pages/CouponPatientPay/CouponPatientPayGPIList";
import ClientLimitsForm from "./pages/PatientPaySchedule/ClientLimitsForm";
import ClientPatientPaySchedule from "./pages/PatientPaySchedule/ClientPatientPaySchedule";
import ClientPatientPayScheduleForm from "./pages/PatientPaySchedule/ClientPatientPayScheduleForm";
import CopyPatientPaySchedule from "./pages/PatientPaySchedule/CopyPatientPaySchedule";
import PatientPaySchedule from "./pages/PatientPaySchedule/PatientPaySchedule";
import PatientPayScheduleCriteria from "./pages/PatientPaySchedule/PatientPayScheduleCriteria";
import PatientPayScheduleCriteriaForm from "./pages/PatientPaySchedule/PatientPayScheduleCriteriaForm";
import PatientPayScheduleForm from "./pages/PatientPaySchedule/PatientPayScheduleForm";
import PatientPayScheduleNote from "./pages/PatientPaySchedule/PatientPayScheduleNote";
import CopyPatientPayTable from "./pages/PatientPayTable/CopyPatientPayTable";
import MinMaxCoPayDifferential from "./pages/PatientPayTable/MinMaxCoPayDifferential";
import MinMaxCoPayStep from "./pages/PatientPayTable/MinMaxCoPayStep";
import MinMaxDifferential from "./pages/PatientPayTable/MinMaxDifferential";
import PatientPayTable from "./pages/PatientPayTable/PatientPayTable";
import PatientPayTableDetailForm from "./pages/PatientPayTable/PatientPayTableDetailForm";
import PatientPayTableNote from "./pages/PatientPayTable/PatientPayTableNote";
import ProcessorFeeTable from "./pages/ProcessorFeeTable/ProcessorFeeTable";

import AdditionalPatientPayStepScheduleForm from "./pages/AdditionalPatientPay/AdditionalPatientPayStepsForm";
import CopayScheduleNote from "./pages/CopaySchedule/CopayScheduleNote";
import CopayScheduleStepForm from "./pages/CopaySchedule/CopayScheduleStepForm";
import CopyCouponPatientPayGpiList from "./pages/CouponPatientPay/CopyCouponPatientPayGpiList";
import CouponPatientPayGPIListForm from "./pages/CouponPatientPay/CouponPatientPayGPIListForm";
import CouponPatientPayGpiListNote from "./pages/CouponPatientPay/CouponPatientPayGpiListNote";
import CouponPatientPayGPIListPlans from "./pages/CouponPatientPay/CouponPatientPayGPIListPlans";
import GpiCouponForm from "./pages/CouponPatientPay/GpiCouponForm";
import GPICouponPatientPay from "./pages/CouponPatientPay/GPICouponPatientPay";
import GpiNoteInCouponPatientPayGpiList from "./pages/CouponPatientPay/GpiNoteInCouponPatientPayGpiList";
import CopyDiscountPatientPayTable from "./pages/DiscountPatientPay/CopyDiscountPatientPayTable";
import DiscountPatientPayTable from "./pages/DiscountPatientPay/DiscountPatientPayTable";
import DiscountPatientPayTableForm from "./pages/DiscountPatientPay/DiscountPatientPayTableForm";
import PharmacyNetwork from "./pages/DiscountPatientPay/PharmacyNetwork";
import PharmacyNetworkForDiscountTable from "./pages/DiscountPatientPay/PharmacyNetworkForDiscountTable";
import PrescriberNetworks from "./pages/DiscountPatientPay/PrescriberNetworks";
import CouponPatientPayGPILists from "./pages/Lookups/CouponPatientPayGPIList/CouponPatientPayGPILists";
import FormularyStatusList from "./pages/Lookups/FormularyStatusList/FormularyStatusList";
import GpiProductId from "./pages/Lookups/GpiScreen/GpiProductId";
import GpiScreen from "./pages/Lookups/GpiScreen/GpiScreen";
import NonStateCostSourceAndType from "./pages/Lookups/NonStateCostSourceAndType/NonStateCostSourceAndType";
import MaxCopaySchedule from "./pages/MaxCopaySchedule/MaxCopaySchedule";
import MaxCopayScheduleForm from "./pages/MaxCopaySchedule/MaxCopayScheduleForm";
import MaxCopayScheduleSteps from "./pages/MaxCopaySchedule/MaxCopayScheduleSteps";
import MaxCopayScheduleStepsForm from "./pages/MaxCopaySchedule/MaxCopayScheduleStepsForm";
import MinMaxCopayDiffrentialStepForm from "./pages/PatientPayTable/MinMaxCopayDifferentialStepForm";
import MinMaxCopayStepForm from "./pages/PatientPayTable/MinMaxCopayStepForm";
import MinMaxDiffrentialStepForm from "./pages/PatientPayTable/MinMaxDifferentialStepForm";
import PatientPayTableAdditionalScheduleForm from "./pages/PatientPayTable/PatientPayTableAdditionalScheduleForm";
import CopyPriceEdgeSchedule from "./pages/PriceEdgeSchedule/CopyPriceEdgeSchedule";

import PriceEdgeSchedule from "./pages/PriceEdgeSchedule/PriceEdgeSchedule";
import PriceEdgeScheduleForm from "./pages/PriceEdgeSchedule/PriceEdgeScheduleForm";

import PriceEdgeScheduleSequences from "./pages/PriceEdgeSchedule/PriceEdgeScheduleSequences";
import CopyProcessorFeeTable from "./pages/ProcessorFeeTable/CopyProcessorFeeTable";
import ProcessorFeeForm from "./pages/ProcessorFeeTable/ProcessorFeeForm";

import SuperNetwork from "./pages/Lookups/SuperNetwork/SuperNetwork";
import PharmacyList from "./pages/Lookups/PharmacyList/PharmacyList";
import PriceEdgeSequenceScheduleForm from "./pages/PriceEdgeSchedule/PriceEdgeSequenceScheduleForm";
import PriceEdgeScheduleNote from "./pages/PriceEdgeSchedule/PriceEdgeScheduleNote";
import GPILookup from "./pages/Lookups/GpiScreen/GpiLookup";

// index.js file
const root = ReactDOM.createRoot(document.getElementById("root"));
const {
  PATIENT_PAY_SCHEDULE,
  COPY_PATIENT_PAY_SCHEDULE,
  CLIENT_PATIENT_PAY_SCHEDULE,
  PATIENT_PAY_SCHEDULE_ADD,
  PATIENT_PAY_SCHEDULE_DISPLAY,
  PATIENT_PAY_SCHEDULE_EDIT,
  PATIENT_PAY_NOTE,
  PATIENT_PAY_CLIENT_LIMITS,
  PATIENT_PAY_TABLE,
  PATIENT_PAY_SCHEDULE_CRITERIA,
  COPY_PATIENT_PAY_TABLE,
  ADD_CLIENT_PATIENT_PAY_SCHEDULE,
  EDIT_CLIENT_PATIENT_PAY_SCHEDULE,
  DISPLAY_CLIENT_PATIENT_PAY_SCHEDULE,
  ADDITIONAL_PATIENT_PAY,
  PATIENT_PAY_TABLE_NOTE,
  PATIENT_PAY_SCHEDULE_CRITERIA_ADD,
  PATIENT_PAY_SCHEDULE_CRITERIA_EDIT,
  PATIENT_PAY_SCHEDULE_CRITERIA_DISPLAY,
  ADDITIONAL_PATIENT_PAY_NOTE,
  COPAY_SCHEDULE,
  ADDITIONAL_PATIENT_PAY_SCHEDULE_ADD,
  ADDITIONAL_PATIENT_PAY_SCHEDULE_DISPLAY,
  ADDITIONAL_PATIENT_PAY_SCHEDULE_EDIT,
  ADD_PATIENT_PAY_TABLE_DETAIL,
  EDIT_PATIENT_PAY_TABLE_DETAIL,
  DISPLAY_PATIENT_PAY_TABLE_DETAIL,
  COPY_COPAY_SCHEDULE,
  COPAY_SCHEDULE_ADD,
  COPAY_SCHEDULE_DISPLAY,
  COPAY_SCHEDULE_EDIT,
  COUPON_PATIENT_PAY,
  PROCESSOR_FEE_TABLE,
  MIN_MAX_COPAY_STEP,
  MIN_MAX_DIFFERENTIAL,
  MIN_MAX_COPAY_DIFFERENTIAL,
  COPAY_SCHEDULE_STEPS,
  ADDITIONAL_PATIENT_PAY_STEPS,
  ADDITIONAL_PATIENT_PAY_STEPS_ADD,
  ADDITIONAL_PATIENT_PAY_STEPS_DISPLAY,
  ADDITIONAL_PATIENT_PAY_STEPS_EDIT,
  COUPON_PATIENT_PAY_ADD,
  COUPON_PATIENT_PAY_EDIT,
  COUPON_PATIENT_PAY_DISPLAY,
  COUPON_PATIENT_PAY_GPI_LIST_NOTE,
  COPAY_SCHEDULE_NOTE,
  COPAY_SCHEDULE_STEPS_ADD,
  COPAY_SCHEDULE_STEPS_DISPLAY,
  COPAY_SCHEDULE_STEPS_EDIT,
  PROCESSOR_FEE_TABLE_ADD,
  PROCESSOR_FEE_TABLE_EDIT,
  PROCESSOR_FEE_TABLE_DISPLAY,
  COUPON_PATIENT_PAY_GPI,
  ADD_MINMAX_COPAY_STEP,
  EDIT_MINMAX_COPAY_STEP,
  DISPLAY_MINMAX_COPAY_STEP,
  ADD_MINMAX_DIFF_STEP,
  EDIT_MINMAX_DIFF_STEP,
  DISPLAY_MINMAX_DIFF_STEP,
  ADD_MINMAX_COPAY_DIFF_STEP,
  EDIT_MINMAX_COPAY_DIFF_STEP,
  DISPLAY_MINMAX_COPAY_DIFF_STEP,
  ADD_PATIENTPAY_TABLE_ADDIITIONAL_SCHEDULE,
  EDIT_PATIENTPAY_TABLE_ADDIITIONAL_SCHEDULE,
  DISPLAY_PATIENTPAY_TABLE_ADDIITIONAL_SCHEDULE,
  ADD_MAX_COPAY_SCHEDULE,
  EDIT_MAX_COPAY_SCHEDULE,
  DISPLAY_MAX_COPAY_SCHEDULE,
  MAX_COPAY_SCHEDULE,
  DISCOUNT_PATIENT_PAY_TABLE,
  DISCOUNT_PATIENT_PAY_TABLE_ADD,
  DISCOUNT_PATIENT_PAY_TABLE_DISPLAY,
  DISCOUNT_PATIENT_PAY_TABLE_EDIT,
  ADD_MAX_COPAY_SCHEDULE_STEPS,
  EDIT_MAX_COPAY_SCHEDULE_STEPS,
  DISPLAY_MAX_COPAY_SCHEDULE_STEPS,
  MAX_COPAY_SCHEDULE_STEPS,
  COPY_COUPON_PATIENT_PAY_GPI_LIST,
  COPY_DISCOUNT_PATIENT_PAY_TABLE,
  COUPON_PATIENT_PAY_GPI_LIST,
  FORMULARY_STATUS_LIST,
  NON_STATE_COST_SOURCE_AND_TYPE,
  NOTE_GPI_COUPON_PATIENT_PAY,
  COPY_PROCESSOR_FEE_TABLE,
  COUPON_PATIENT_PAY_GPI_LIST_PLANS,
  ADD_COUPON_PATIENT_PAY_GPI_LIST,
  EDIT_COUPON_PATIENT_PAY_GPI_LIST,
  DISPLAY_COUPON_PATIENT_PAY_GPI_LIST,
  PRICE_EDGE_SCHEDULE,
  PRESCRIBER_NETWORKS,
  GPI_SCREEN,
  GPI_PRODUCT_ID,
  PHARMACY_LIST,
  PHARMACY,
  SUPER_NETWORK_LOOKUP,
  PHARMACY_NETWORKS,
  PRICE_EDGE_SCHEDULE_SEQUENCE,

  ADD_PRICE_EDGE_SCHEDULE,
  EDIT_PRICE_EDGE_SCHEDULE,
  DISPLAY_PRICE_EDGE_SCHEDULE,
  ADD_PRICE_EDGE_SCHEDULE_SEQUENCE,
  EDIT_PRICE_EDGE_SCHEDULE_SEQUENCE,
  DISPLAY_PRICE_EDGE_SCHEDULE_SEQUENCE,
  COPY_PRICE_EDGE_SCHEDULE,
  PRICE_EDGE_SCHEDULE_NOTE,
  EXTERNAL_GPI_LOOKUP
} = ROUTE_PATHS;

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route
      path="/"
      element={
        <ModalProvider>
          <LoaderProvider>
            <App />
          </LoaderProvider>
        </ModalProvider>
      }
    >
      {/* <Route path={PRICE_SCHEDULE} element={<PriceSchedule />} /> */}
      <Route path={PATIENT_PAY_SCHEDULE} element={<PatientPaySchedule />} />
      <Route
        path={CLIENT_PATIENT_PAY_SCHEDULE}
        element={<ClientPatientPaySchedule />}
      />
      <Route
        path={PATIENT_PAY_CLIENT_LIMITS}
        element={<ClientLimitsForm mode={PRICE_SCHEDULE_MODES.ADD} />}
      />
      <Route path={PATIENT_PAY_NOTE} element={<PatientPayScheduleNote />} />
      <Route path={PATIENT_PAY_TABLE_NOTE} element={<PatientPayTableNote />} />
      <Route path={PRICE_EDGE_SCHEDULE_NOTE} element={<PriceEdgeScheduleNote />} />

      <Route path={PATIENT_PAY_TABLE} element={<PatientPayTable />} />
      <Route path={NOTE_GPI_COUPON_PATIENT_PAY} element={<GpiNoteInCouponPatientPayGpiList />} />
      <Route
        path={PATIENT_PAY_SCHEDULE_CRITERIA}
        element={<PatientPayScheduleCriteria />}
      />
      <Route
        path={ADDITIONAL_PATIENT_PAY_NOTE}
        element={<AdditionalPatientPayNotes />}
      />
      <Route
        path={COUPON_PATIENT_PAY_GPI_LIST_NOTE}
        element={<CouponPatientPayGpiListNote />}
      />

      <Route
        path={PATIENT_PAY_SCHEDULE_ADD}
        element={<PatientPayScheduleForm mode={PRICE_SCHEDULE_MODES.ADD} />}
      />
      <Route
        path={PATIENT_PAY_SCHEDULE_DISPLAY}
        element={
          <PatientPayScheduleForm mode={PRICE_SCHEDULE_MODES.READ_ONLY} />
        }
      />
      <Route
        path={PATIENT_PAY_SCHEDULE_EDIT}
        element={<PatientPayScheduleForm mode={PRICE_SCHEDULE_MODES.EDIT} />}
      />

      <Route
        path={COPY_PATIENT_PAY_SCHEDULE}
        element={<CopyPatientPaySchedule mode={PRICE_SCHEDULE_MODES.ADD} />}
      />

      <Route
        path={COPY_PRICE_EDGE_SCHEDULE}
        element={<CopyPriceEdgeSchedule mode={PRICE_SCHEDULE_MODES.ADD} />}
      />
      <Route
        path={COPY_COPAY_SCHEDULE}
        element={<CopyCopaySchedule mode={PRICE_SCHEDULE_MODES.ADD} />}
      />
      <Route
        path={COPY_PROCESSOR_FEE_TABLE}
        element={<CopyProcessorFeeTable mode={PRICE_SCHEDULE_MODES.ADD} />}
      />

      <Route
        path={COPY_PATIENT_PAY_TABLE}
        element={<CopyPatientPayTable mode={PRICE_SCHEDULE_MODES.ADD} />}
      />
      <Route
        path={COPY_COUPON_PATIENT_PAY_GPI_LIST}
        element={<CopyCouponPatientPayGpiList mode={PRICE_SCHEDULE_MODES.ADD} />}
      />

      <Route
        path={COPY_DISCOUNT_PATIENT_PAY_TABLE}
        element={<CopyDiscountPatientPayTable mode={PRICE_SCHEDULE_MODES.ADD} />}
      />



      <Route
        path={ADD_CLIENT_PATIENT_PAY_SCHEDULE}
        element={
          <ClientPatientPayScheduleForm mode={PRICE_SCHEDULE_MODES.ADD} />
        }
      />

      <Route
        path={EDIT_CLIENT_PATIENT_PAY_SCHEDULE}
        element={
          <ClientPatientPayScheduleForm mode={PRICE_SCHEDULE_MODES.EDIT} />
        }
      />

      <Route
        path={DISPLAY_CLIENT_PATIENT_PAY_SCHEDULE}
        element={
          <ClientPatientPayScheduleForm mode={PRICE_SCHEDULE_MODES.READ_ONLY} />
        }
      />

      <Route path={ADDITIONAL_PATIENT_PAY} element={<AdditionalPatientPay />} />
      <Route path={DISCOUNT_PATIENT_PAY_TABLE} element={<DiscountPatientPayTable />} />
      <Route
        path={PATIENT_PAY_SCHEDULE_CRITERIA_ADD}
        element={
          <PatientPayScheduleCriteriaForm mode={PRICE_SCHEDULE_MODES.ADD} />
        }
      />
      <Route
        path={PATIENT_PAY_SCHEDULE_CRITERIA_DISPLAY}
        element={
          <PatientPayScheduleCriteriaForm
            mode={PRICE_SCHEDULE_MODES.READ_ONLY}
          />
        }
      />
      <Route
        path={PATIENT_PAY_SCHEDULE_CRITERIA_EDIT}
        element={
          <PatientPayScheduleCriteriaForm mode={PRICE_SCHEDULE_MODES.EDIT} />
        }
      />
      <Route path={COPAY_SCHEDULE} element={<CopaySchedule />} />

      <Route
        path={PATIENT_PAY_SCHEDULE_CRITERIA_EDIT}
        element={
          <PatientPayScheduleCriteriaForm mode={PRICE_SCHEDULE_MODES.EDIT} />
        }
      />
      <Route path={ADDITIONAL_PATIENT_PAY_STEPS} element={<AdditionalPatientPaySteps />} />
      <Route
        path={ADDITIONAL_PATIENT_PAY_SCHEDULE_ADD}
        element={
          <AdditionalPatientPayScheduleForm mode={PRICE_SCHEDULE_MODES.ADD} />
        }
      />
      <Route
        path={ADDITIONAL_PATIENT_PAY_SCHEDULE_DISPLAY}
        element={
          <AdditionalPatientPayScheduleForm
            mode={PRICE_SCHEDULE_MODES.READ_ONLY}
          />
        }
      />
      <Route
        path={ADDITIONAL_PATIENT_PAY_SCHEDULE_EDIT}
        element={
          <AdditionalPatientPayScheduleForm mode={PRICE_SCHEDULE_MODES.EDIT} />
        }
      />
      <Route path={COPAY_SCHEDULE_NOTE} element={<CopayScheduleNote />} />
      <Route
        path={ADD_PATIENT_PAY_TABLE_DETAIL}
        element={
          <PatientPayTableDetailForm mode={PRICE_SCHEDULE_MODES.ADD} />
        }
      />

      <Route
        path={EDIT_PATIENT_PAY_TABLE_DETAIL}
        element={
          <PatientPayTableDetailForm mode={PRICE_SCHEDULE_MODES.EDIT} />
        }
      />

      <Route
        path={DISPLAY_PATIENT_PAY_TABLE_DETAIL}
        element={
          <PatientPayTableDetailForm mode={PRICE_SCHEDULE_MODES.READ_ONLY} />
        }
      />


      <Route path={COPAY_SCHEDULE_NOTE} element={<CopayScheduleNote />} />

      <Route
        path={COPAY_SCHEDULE_ADD}
        element={<CopayScheduleForm key="add" mode={PRICE_SCHEDULE_MODES.ADD} />}
      />
      <Route
        path={COPAY_SCHEDULE_DISPLAY}
        element={<CopayScheduleForm key="display" mode={PRICE_SCHEDULE_MODES.READ_ONLY} />}

      />
      <Route
        path={COPAY_SCHEDULE_EDIT}
        element={<CopayScheduleForm key="edit" mode={PRICE_SCHEDULE_MODES.EDIT} />}
      />
      <Route path={COUPON_PATIENT_PAY} element={<CouponPatientPayGPIList />} />
      <Route
        path={PROCESSOR_FEE_TABLE}
        element={<ProcessorFeeTable />}
      />
      <Route path={MIN_MAX_COPAY_STEP} element={<MinMaxCoPayStep />} />
      <Route path={MIN_MAX_DIFFERENTIAL} element={<MinMaxDifferential />} />
<Route path={MIN_MAX_COPAY_DIFFERENTIAL} element={<MinMaxCoPayDifferential />} />
      <Route
        path={COPAY_SCHEDULE_STEPS}
        element={<CopayScheduleStep />}
      />
            <Route
        path={ADDITIONAL_PATIENT_PAY_STEPS_ADD}
        element={
          <AdditionalPatientPayStepScheduleForm mode={PRICE_SCHEDULE_MODES.ADD} />
        }
      />
      <Route
        path={ADDITIONAL_PATIENT_PAY_STEPS_DISPLAY}
        element={
          <AdditionalPatientPayStepScheduleForm mode={PRICE_SCHEDULE_MODES.READ_ONLY} />
        }
      />
      <Route
        path={ADDITIONAL_PATIENT_PAY_STEPS_EDIT}
        element={
          <AdditionalPatientPayStepScheduleForm mode={PRICE_SCHEDULE_MODES.EDIT} />
        }
      />
      <Route
        path={COUPON_PATIENT_PAY_ADD}
        element={
          <CouponPatientPayGPIListForm mode={PRICE_SCHEDULE_MODES.ADD} />
        }
      />
      <Route
        path={COUPON_PATIENT_PAY_DISPLAY}
        element={
          <CouponPatientPayGPIListForm mode={PRICE_SCHEDULE_MODES.READ_ONLY} />
        }
      />
      <Route
        path={COUPON_PATIENT_PAY_EDIT}
        element={
          <CouponPatientPayGPIListForm mode={PRICE_SCHEDULE_MODES.EDIT} />
        }
      />
      <Route path={COUPON_PATIENT_PAY} element={<CouponPatientPayGPIList />} />

      <Route
        path={COPAY_SCHEDULE_STEPS_ADD}
        element={
          <CopayScheduleStepForm mode={PRICE_SCHEDULE_MODES.ADD} />
        }
      />
      <Route
        path={COPAY_SCHEDULE_STEPS_DISPLAY}
        element={
          <CopayScheduleStepForm mode={PRICE_SCHEDULE_MODES.READ_ONLY} />
        }
      />
      <Route
        path={COPAY_SCHEDULE_STEPS_EDIT}
        element={
          <CopayScheduleStepForm mode={PRICE_SCHEDULE_MODES.EDIT} />
        }
      />
      <Route
        path={PROCESSOR_FEE_TABLE_ADD}
        element={
          <ProcessorFeeForm mode={PRICE_SCHEDULE_MODES.ADD} />
        }
      />
      <Route
        path={PROCESSOR_FEE_TABLE_DISPLAY}
        element={
          <ProcessorFeeForm mode={PRICE_SCHEDULE_MODES.READ_ONLY} />
        }
      />
      <Route
        path={PROCESSOR_FEE_TABLE_EDIT}
        element={
          <ProcessorFeeForm mode={PRICE_SCHEDULE_MODES.EDIT} />
        }
      />
      <Route
        path={COUPON_PATIENT_PAY_GPI}
        element={
          <GPICouponPatientPay />
        }
      />

      <Route
        path={ADD_MINMAX_COPAY_STEP}
        element={
          <MinMaxCopayStepForm mode={PRICE_SCHEDULE_MODES.ADD} />
        }
      />

      <Route
        path={EDIT_MINMAX_COPAY_STEP}
        element={
          <MinMaxCopayStepForm mode={PRICE_SCHEDULE_MODES.EDIT} />
        }
      />

      <Route
        path={DISPLAY_MINMAX_COPAY_STEP}
        element={
          <MinMaxCopayStepForm mode={PRICE_SCHEDULE_MODES.READ_ONLY} />
        }
      />

      <Route
        path={ADD_MINMAX_COPAY_DIFF_STEP}
        element={
          <MinMaxCopayDiffrentialStepForm mode={PRICE_SCHEDULE_MODES.ADD} />
        }
      />

      <Route
        path={EDIT_MINMAX_COPAY_DIFF_STEP}
        element={
          <MinMaxCopayDiffrentialStepForm mode={PRICE_SCHEDULE_MODES.EDIT} />
        }
      />

      <Route
        path={DISPLAY_MINMAX_COPAY_DIFF_STEP}
        element={
          <MinMaxCopayDiffrentialStepForm mode={PRICE_SCHEDULE_MODES.READ_ONLY} />
        }
      />

      <Route
        path={ADD_MINMAX_DIFF_STEP}
        element={
          <MinMaxDiffrentialStepForm mode={PRICE_SCHEDULE_MODES.ADD} />
        }
      />

      <Route
        path={EDIT_MINMAX_DIFF_STEP}
        element={
          <MinMaxDiffrentialStepForm mode={PRICE_SCHEDULE_MODES.EDIT} />
        }
      />

      <Route
        path={DISPLAY_MINMAX_DIFF_STEP}
        element={
          <MinMaxDiffrentialStepForm mode={PRICE_SCHEDULE_MODES.READ_ONLY} />
        }
      />

      <Route
        path={ADD_PATIENTPAY_TABLE_ADDIITIONAL_SCHEDULE}
        element={
          <PatientPayTableAdditionalScheduleForm mode={PRICE_SCHEDULE_MODES.ADD} />
        }
      />

      <Route
        path={EDIT_PATIENTPAY_TABLE_ADDIITIONAL_SCHEDULE}
        element={
          <PatientPayTableAdditionalScheduleForm mode={PRICE_SCHEDULE_MODES.EDIT} />
        }
      />

      <Route
        path={DISPLAY_PATIENTPAY_TABLE_ADDIITIONAL_SCHEDULE}
        element={
          <PatientPayTableAdditionalScheduleForm mode={PRICE_SCHEDULE_MODES.READ_ONLY} />
        }
      />
      <Route
        path={ADD_MAX_COPAY_SCHEDULE}
        element={
          <MaxCopayScheduleForm mode={PRICE_SCHEDULE_MODES.ADD} />
        }
      />

      <Route
        path={EDIT_MAX_COPAY_SCHEDULE}
        element={
          <MaxCopayScheduleForm mode={PRICE_SCHEDULE_MODES.EDIT} />
        }
      />

      <Route
        path={DISPLAY_MAX_COPAY_SCHEDULE}
        element={
          <MaxCopayScheduleForm mode={PRICE_SCHEDULE_MODES.READ_ONLY} />
        }
      />
      <Route
        path={MAX_COPAY_SCHEDULE}
        element={
          <MaxCopaySchedule />
        }
      />

      <Route
        path={DISCOUNT_PATIENT_PAY_TABLE_ADD}
        element={
          <DiscountPatientPayTableForm mode={PRICE_SCHEDULE_MODES.ADD} />
        }
      />
      <Route
        path={DISCOUNT_PATIENT_PAY_TABLE_DISPLAY}
        element={
          <DiscountPatientPayTableForm mode={PRICE_SCHEDULE_MODES.READ_ONLY} />
        }
      />
      <Route
        path={DISCOUNT_PATIENT_PAY_TABLE_EDIT}
        element={
          <DiscountPatientPayTableForm mode={PRICE_SCHEDULE_MODES.EDIT} />
        }
      />
      <Route
        path={ADD_MAX_COPAY_SCHEDULE_STEPS}
        element={
          <MaxCopayScheduleStepsForm mode={PRICE_SCHEDULE_MODES.ADD} />
        }
      />

      <Route
        path={EDIT_MAX_COPAY_SCHEDULE_STEPS}
        element={
          <MaxCopayScheduleStepsForm mode={PRICE_SCHEDULE_MODES.EDIT} />
        }
      />

      <Route
        path={DISPLAY_MAX_COPAY_SCHEDULE_STEPS}
        element={
          <MaxCopayScheduleStepsForm mode={PRICE_SCHEDULE_MODES.READ_ONLY} />
        }
      />
      <Route
        path={MAX_COPAY_SCHEDULE_STEPS}
        element={
          <MaxCopayScheduleSteps />
        }
      />
      <Route
        path={PHARMACY_NETWORKS}
        element={
          <PharmacyNetworkForDiscountTable />
        }
      />

      <Route
        path={COUPON_PATIENT_PAY_GPI_LIST}
        element={
          <CouponPatientPayGPILists />
        }
      />

      <Route
        path={FORMULARY_STATUS_LIST}
        element={
          <FormularyStatusList />
        }
      />

      <Route
        path={NON_STATE_COST_SOURCE_AND_TYPE}
        element={
          <NonStateCostSourceAndType />
        }
      />
      <Route
        path={ADD_COUPON_PATIENT_PAY_GPI_LIST}
        element={
          <GpiCouponForm mode={PRICE_SCHEDULE_MODES.ADD} />
        }
      />

      <Route
        path={EDIT_COUPON_PATIENT_PAY_GPI_LIST}
        element={
          <GpiCouponForm mode={PRICE_SCHEDULE_MODES.EDIT} />
        }
      />

      <Route
        path={DISPLAY_COUPON_PATIENT_PAY_GPI_LIST}
        element={
          <GpiCouponForm mode={PRICE_SCHEDULE_MODES.READ_ONLY} />
        }
      />
      <Route
        path={COUPON_PATIENT_PAY_GPI_LIST_PLANS}
        element={
          <CouponPatientPayGPIListPlans />
        }
      />
      <Route
        path={PRICE_EDGE_SCHEDULE}
        element={<PriceEdgeSchedule />}
      />
      <Route
        path={PRESCRIBER_NETWORKS}
        element={
          <PrescriberNetworks />
        }
      />
      <Route
        path={GPI_SCREEN}
        element={
          <GpiScreen />
        }
      />
      <Route
        path={GPI_PRODUCT_ID}
        element={
          <GpiProductId />
        }
      />
      <Route
        path={PHARMACY_NETWORKS}
        element={
          <PharmacyNetworkForDiscountTable />
        }
      />
      <Route
        path={PHARMACY_LIST}
        element={
          <PharmacyList />
        }
      />
      <Route
        path={PHARMACY}
        element={
          <PharmacyNetwork />
        }
      />
      <Route
        path={SUPER_NETWORK_LOOKUP}
        element={
          <SuperNetwork />
        }
      />


      <Route
        path={PRICE_EDGE_SCHEDULE_SEQUENCE}
        element={
          <PriceEdgeScheduleSequences />
        }
      />

      <Route
        path={ADD_PRICE_EDGE_SCHEDULE}
        element={
          <PriceEdgeScheduleForm mode={PRICE_SCHEDULE_MODES.ADD} />
        }
      />

      <Route
        path={EDIT_PRICE_EDGE_SCHEDULE}
        element={
          <PriceEdgeScheduleForm mode={PRICE_SCHEDULE_MODES.EDIT} />
        }
      />
      <Route
        path={DISPLAY_PRICE_EDGE_SCHEDULE}
        element={
          <PriceEdgeScheduleForm mode={PRICE_SCHEDULE_MODES.READ_ONLY} />
        }
      />
      <Route
        path={ADD_PRICE_EDGE_SCHEDULE_SEQUENCE}
        element={
          <PriceEdgeSequenceScheduleForm mode={PRICE_SCHEDULE_MODES.ADD} />
        }
      />

      <Route
        path={EDIT_PRICE_EDGE_SCHEDULE_SEQUENCE}
        element={
          <PriceEdgeSequenceScheduleForm mode={PRICE_SCHEDULE_MODES.EDIT} />
        }
      />
      <Route
        path={DISPLAY_PRICE_EDGE_SCHEDULE_SEQUENCE}
        element={
          <PriceEdgeSequenceScheduleForm mode={PRICE_SCHEDULE_MODES.READ_ONLY} />
        }
      />
      <Route path={EXTERNAL_GPI_LOOKUP} element={<GPILookup />} />
    </Route>
  )
);

root.render(
  <AuthProvider>
    <RBACProvider>
      <PrimeReactProvider>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </PrimeReactProvider>
    </RBACProvider>
  </AuthProvider>
);

reportWebVitals();